import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import type { ArticleSummary } from '@/types/newsService';
import { cn } from '@/lib/cn';
import { useHaptics } from '@/hooks/useHaptics';

interface Props {
  articles: ArticleSummary[];
  locale?: string;
}

const CARD_HEIGHT = 220;

/**
 * Snap-paginated horizontal carousel of featured articles. Hero image with a
 * dark gradient overlay, primary tag, title, subtitle.
 *
 * Web port of FlatList horizontal with snapToInterval: we use a flex
 * container with `scroll-snap-type: x mandatory` and watch scroll position
 * for the active dot indicator.
 */
export const FeaturedCarousel = memo(function FeaturedCarousel({
  articles,
  locale = 'en',
}: Props) {
  const navigate = useNavigate();
  const haptics = useHaptics();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  // Track active card by scroll position.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const onScroll = () => {
      const cardWidth = el.scrollWidth / Math.max(articles.length, 1);
      const idx = Math.round(el.scrollLeft / Math.max(cardWidth, 1));
      setActiveIndex(idx);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [articles.length]);

  const handleNavigate = useCallback(
    (slug: string) => {
      haptics.selection();
      navigate(`/news/${slug}`);
    },
    [navigate, haptics],
  );

  if (articles.length === 0) return null;

  return (
    <div className="mb-2">
      <div
        ref={scrollRef}
        className="no-scrollbar overflow-x-auto"
        style={{ scrollSnapType: 'x mandatory' }}
      >
        <div className="flex gap-4 px-4">
          {articles.map((item) => {
            const localized = item.content.find((c) => c.locale === locale) ?? item.content[0];
            if (!localized) return null;
            return (
              <button
                key={item._id}
                type="button"
                onClick={() => handleNavigate(item.slug)}
                aria-label={localized.title}
                className="card-edge relative shrink-0 overflow-hidden rounded-2xl bg-bg-secondary text-left transition-opacity active:opacity-80"
                style={{
                  width: 'calc(100vw - 2rem)',
                  height: CARD_HEIGHT,
                  scrollSnapAlign: 'start',
                }}
              >
                {item.heroImage && (
                  <img
                    src={item.heroImage.url}
                    alt={item.heroImage.alt}
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                )}
                <div
                  className="pointer-events-none absolute inset-0"
                  style={{
                    background:
                      'linear-gradient(to bottom, transparent 30%, rgba(0,0,0,0.8) 100%)',
                  }}
                />
                <div className="absolute inset-x-0 bottom-0 p-4">
                  {item.primaryTag && (
                    <span className="mb-2 inline-block rounded-lg bg-brand/20 px-2 py-[3px] text-[10px] font-bold uppercase text-brand">
                      {item.primaryTag.name}
                    </span>
                  )}
                  <h3 className="line-clamp-2 text-xl font-bold leading-[26px] text-white">
                    {localized.title}
                  </h3>
                  {localized.subtitle ? (
                    <p className="mt-1 line-clamp-1 text-sm text-white/70">
                      {localized.subtitle}
                    </p>
                  ) : null}
                </div>
              </button>
            );
          })}
        </div>
      </div>
      {articles.length > 1 && (
        <div className="mt-3 flex justify-center gap-1.5">
          {articles.map((_, i) => (
            <span
              key={i}
              className={cn(
                'h-1.5 rounded-full transition-all duration-200',
                i === activeIndex
                  ? 'w-[18px] bg-brand'
                  : 'w-1.5 bg-bg-tertiary',
              )}
            />
          ))}
        </div>
      )}
    </div>
  );
});
